// src/components/CoverPage/InvestmentLandingPage.jsx
"use client";

import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { ethers } from "ethers";
import HeroSection from "./HeroSection.jsx";
import HowItWorksSection from "./HowItWorksSection.jsx";
import FeaturesSection from "./FeaturesSection.jsx";
import TokenSaleSection from "./TokenSaleSection.jsx";
import RoadmapSection from "./RoadmapSection.jsx"; // Import the new RoadmapSection
import CtaSection from "./CtaSection.jsx";

const InvestmentLandingPage = () => {
  const [isWalletModalOpen, setIsWalletModalOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Web3 state
  const [web3Connected, setWeb3Connected] = useState(false);
  const [provider, setProvider] = useState(null);
  const [account, setAccount] = useState("");
  
  // Check if wallet is already connected on page load
  useEffect(() => {
    const checkWalletConnection = async () => {
      if (window.ethereum) {
        try {
          // Create provider
          const provider = new ethers.providers.Web3Provider(window.ethereum);
          setProvider(provider);
          
          // Check if already connected
          const accounts = await provider.listAccounts();
          if (accounts.length > 0) {
            setAccount(accounts[0]);
            setWeb3Connected(true);
            console.log("Wallet already connected:", accounts[0]);
          }
        } catch (error) {
          console.error("Error checking wallet connection:", error);
        }
      }
    };
    
    checkWalletConnection();
  }, []);
  
  // Listen for account changes
  useEffect(() => {
    if (window.ethereum) {
      const handleAccountsChanged = (accounts) => {
        if (accounts.length > 0) {
          setAccount(accounts[0]);
          setWeb3Connected(true);
          console.log("Connected account changed:", accounts[0]);
        } else {
          setAccount("");
          setWeb3Connected(false);
          console.log("Wallet disconnected");
        }
      };
      
      // Subscribe to accounts change
      window.ethereum.on("accountsChanged", handleAccountsChanged);
      
      // Clean up listener
      return () => {
        window.ethereum.removeListener("accountsChanged", handleAccountsChanged);
      };
    }
  }, []);

  function toggleWalletModal() {
    setIsWalletModalOpen(!isWalletModalOpen);
  }
  
  // Function to connect wallet
  async function connectWallet(walletType) {
    try {
      if (window.ethereum) {
        // Create provider
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        setProvider(provider);
        
        // Request accounts access
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        setAccount(accounts[0]);
        setWeb3Connected(true);
        console.log(`Connected to wallet (${walletType}):`, accounts[0]);
        
        // Close modal
        setIsWalletModalOpen(false);
      } else {
        console.error("No Ethereum provider found. Please install MetaMask or another wallet.");
        alert("No Ethereum provider found. Please install MetaMask or another wallet.");
      }
    } catch (error) {
      console.error("Error connecting wallet:", error);
    }
  }

  // Function to navigate directly to the app
  function navigateToApp() {
    // Navigate to the app route
    navigate("/app");
  }
  
  // Function to navigate to learn more page
  function navigateToLearn() {
    // Navigate to the learn more route
    navigate("/learn");
  }
  
  // Function to navigate to documentation
  function navigateToDocs() {
    // Navigate to the local documentation page
    navigate("/docs");
  }
  
  // Function to navigate to FAQ
  function navigateToFAQ() {
    // Navigate to the FAQ section
    // For now, let's just scroll to the FAQ section on the same page
    const faqSection = document.getElementById("faq-section");
    if (faqSection) {
      faqSection.scrollIntoView({ behavior: "smooth" });
    }
  }
  
  // Format Ethereum address for display
  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  return (
    <main className="mx-auto my-0 w-full text-zinc-900">
      {/* Navigation Bar */}
      <nav className="bg-blue-900 text-white shadow-md sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center py-4">
            {/* Logo and Brand */}
            <div className="flex items-center space-x-4">
              <Link to="/" className="text-2xl font-bold">YildX</Link>
            </div>
            
            {/* Navigation Links */}
            <div className="hidden md:flex space-x-1">
              <NavLink 
                to="/" 
                active={location.pathname === '/'} 
                label="Home" 
              />
              <NavLink 
                to="/app" 
                active={location.pathname === '/app'} 
                label="App" 
              />
              <NavLink 
                to="/delegation" 
                active={location.pathname === '/delegation'} 
                label="Delegation" 
              />
              <NavLink 
                to="/learn" 
                active={location.pathname === '/learn'} 
                label="Learn More" 
              />
              <NavLink 
                to="#faq-section" 
                active={false} 
                label="FAQ" 
                onClick={(e) => {
                  e.preventDefault();
                  navigateToFAQ();
                }}
              />
              <NavLink 
                to="/docs" 
                active={location.pathname === '/docs'} 
                label="Docs" 
              />
            </div>
            
            {/* Connect Button */}
            <div className="flex items-center">
              {web3Connected ? (
                <div className="flex items-center space-x-2">
                  <div className="bg-green-500 rounded-full w-2 h-2"></div>
                  <span className="text-sm">{formatAddress(account)}</span>
                </div>
              ) : (
                <button 
                  onClick={toggleWalletModal}
                  className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm"
                >
                  Connect Wallet
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Landing Page Content */}
      <HeroSection navigateToApp={navigateToApp} />
      <HowItWorksSection />
      <FeaturesSection />
      <TokenSaleSection 
        toggleWalletModal={toggleWalletModal}
        web3Connected={web3Connected}
        provider={provider}
        account={account}
      />
      <RoadmapSection /> {/* Add the new RoadmapSection component here */}
      
      {/* FAQ Section */}
      <section id="faq-section" className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
          
          <div className="max-w-3xl mx-auto space-y-6">
            <FAQItem 
              question="What is YildX Protocol?" 
              answer="YildX is a decentralized asset management protocol that allows users to invest in tokenized crypto index funds. It provides diversified exposure to digital assets through a simple, transparent, and fully automated structure." 
            />
            
            <FAQItem 
              question="How do I invest in YildX funds?" 
              answer="You can invest by connecting your wallet, depositing ETH, and minting MCIF tokens. These tokens represent your proportional share of the underlying assets in the fund." 
            />
            
            <FAQItem 
              question="What is token delegation?" 
              answer="Token delegation allows you to assign your voting power to another address without transferring ownership of your tokens. This enables you to participate in governance without actively voting on proposals." 
            />
            
            <FAQItem 
              question="How are the funds managed?" 
              answer="YildX funds are managed through smart contracts that automatically allocate assets according to predefined strategies. Token holders can participate in governance to influence fund parameters." 
            />
            
            <FAQItem 
              question="What are the fees?" 
              answer="YildX charges a small management fee (2% annually), entry fee (0.25%), and exit fee (0.25%). These fees are transparently displayed and can be adjusted through governance." 
            />
          </div>
        </div>
      </section>
      
      <CtaSection navigateToApp={navigateToApp} />

      {/* Wallet Connect Modal */}
      {isWalletModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={toggleWalletModal}
        >
          <div
            className="bg-white p-8 rounded-lg max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold mb-4">Connect Your Wallet</h2>
            <p className="mb-6">
              Choose your preferred wallet provider to continue.
            </p>
            <div className="space-y-4">
              <button 
                className="w-full p-4 border border-gray-200 rounded-lg flex items-center justify-between hover:bg-gray-50"
                onClick={() => connectWallet('metamask')}
              >
                <span className="font-medium">MetaMask</span>
                <span>→</span>
              </button>
              <button 
                className="w-full p-4 border border-gray-200 rounded-lg flex items-center justify-between hover:bg-gray-50"
                onClick={() => connectWallet('walletconnect')}
              >
                <span className="font-medium">WalletConnect</span>
                <span>→</span>
              </button>
              <button 
                className="w-full p-4 border border-gray-200 rounded-lg flex items-center justify-between hover:bg-gray-50"
                onClick={() => connectWallet('coinbase')}
              >
                <span className="font-medium">Coinbase Wallet</span>
                <span>→</span>
              </button>
            </div>
            <button
              className="mt-6 w-full p-3 bg-gray-200 rounded-lg font-medium"
              onClick={toggleWalletModal}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {/* Footer */}
      <footer className="bg-blue-900 text-white py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <h3 className="text-xl font-bold">YildX Protocol</h3>
              <p className="text-blue-200 mt-1">Decentralized Asset Management</p>
            </div>
            <div className="flex space-x-6">
              <Link to="/" className="text-blue-200 hover:text-white">Home</Link>
              <Link to="/app" className="text-blue-200 hover:text-white">App</Link>
              <Link to="/delegation" className="text-blue-200 hover:text-white">Delegation</Link>
              <Link to="/learn" className="text-blue-200 hover:text-white">Learn More</Link>
              <Link to="/docs" className="text-blue-200 hover:text-white">Docs</Link>
            </div>
          </div>
          <div className="mt-8 pt-4 border-t border-blue-800 text-center text-blue-300 text-sm">
            © {new Date().getFullYear()} YildX Protocol. All rights reserved.
          </div>
        </div>
      </footer>
    </main>
  );
};

// FAQ Item Component
const FAQItem = ({ question, answer }) => {
  return (
    <div className="bg-white shadow-sm rounded-lg p-6">
      <h3 className="text-xl font-semibold text-blue-800 mb-3">{question}</h3>
      <p className="text-gray-700">{answer}</p>
    </div>
  );
};

// NavLink component for consistent styling
const NavLink = ({ to, active, label, onClick, external = false }) => {
  const baseClassName = `px-4 py-2 rounded-md text-sm font-medium ${
    active 
      ? 'bg-blue-800 text-white' 
      : 'text-gray-200 hover:bg-blue-800 hover:text-white'
  }`;
  
  if (onClick || external) {
    return (
      <a
        href={to}
        className={baseClassName}
        onClick={onClick}
        target={external ? "_blank" : ""}
        rel={external ? "noopener noreferrer" : ""}
      >
        {label}
      </a>
    );
  }
  
  return (
    <Link
      to={to}
      className={baseClassName}
    >
      {label}
    </Link>
  );
};

export default InvestmentLandingPage;